import type { DesignSpec, Part, Joint } from '@shared/spec';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronRight, Box, Wrench, Ruler, Layers } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SpecTreeProps {
  spec: DesignSpec | null | undefined;
  className?: string;
}

export function SpecTree({ spec, className }: SpecTreeProps) {
  const { t } = useTranslation('common');
  if (!spec) {
    return (
      <div className={cn('text-sm text-muted-foreground p-3', className)}>
        {t('spec.noStructured')}
      </div>
    );
  }

  return (
    <div className={cn('text-sm space-y-1 font-mono', className)}>
      <Section
        icon={<Box className="w-3 h-3" />}
        label={`${t('spec.treeParts')} (${spec.parts.length})`}
        defaultOpen
      >
        {spec.parts.map((p) => (
          <PartRow key={p.id} part={p} spec={spec} />
        ))}
      </Section>
      <Section
        icon={<Wrench className="w-3 h-3" />}
        label={`${t('spec.treeJoints')} (${spec.joints.length})`}
        defaultOpen={spec.joints.length > 0}
      >
        {spec.joints.length === 0 ? (
          <div className="text-xs text-muted-foreground pl-5">{t('spec.noJoints')}</div>
        ) : (
          spec.joints.map((j) => <JointRow key={j.id} joint={j} />)
        )}
      </Section>
      <Section
        icon={<Ruler className="w-3 h-3" />}
        label={`${t('spec.treeParameters')} (${spec.parameters.length})`}
      >
        {spec.parameters.length === 0 ? (
          <div className="text-xs text-muted-foreground pl-5">{t('spec.noParameters')}</div>
        ) : (
          spec.parameters.map((p) => (
            <div key={p.name} className="pl-5 text-xs">
              <span className="text-brand-indigo">{p.name}</span>
              <span className="text-muted-foreground"> = </span>
              <span>{JSON.stringify(p.value)}</span>
              {p.displayName && (
                <span className="text-muted-foreground ml-2">({p.displayName})</span>
              )}
            </div>
          ))
        )}
      </Section>
      <Section
        icon={<Layers className="w-3 h-3" />}
        label={`${t('spec.treeMaterials')} (${spec.materials.length})`}
      >
        {spec.materials.length === 0 ? (
          <div className="text-xs text-muted-foreground pl-5">{t('spec.noMaterials')}</div>
        ) : (
          spec.materials.map((m) => (
            <div key={m.id} className="pl-5 text-xs">
              <span className="text-brand-indigo">{m.id}</span>
              <span className="text-muted-foreground"> · </span>
              <span>{m.name}</span>
              <span className="text-muted-foreground"> · </span>
              <span>{m.kind}</span>
              {m.thickness && (
                <span className="text-muted-foreground"> · {m.thickness}mm</span>
              )}
            </div>
          ))
        )}
      </Section>
    </div>
  );
}

function Section({
  icon,
  label,
  children,
  defaultOpen = false,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs font-semibold hover:text-primary transition"
      >
        {open ? (
          <ChevronDown className="w-3 h-3" />
        ) : (
          <ChevronRight className="w-3 h-3" />
        )}
        {icon}
        {label}
      </button>
      {open && <div className="mt-1 space-y-0.5">{children}</div>}
    </div>
  );
}

function PartRow({ part, spec }: { part: Part; spec: DesignSpec }) {
  const { t } = useTranslation('common');
  const material = spec.materials.find((m) => m.id === part.materialId);
  const dims = partDims(part, t);
  return (
    <div className="pl-5 text-xs">
      <span className="text-green-600 dark:text-green-400">{part.id}</span>
      <span className="text-muted-foreground"> · </span>
      <span>{part.name}</span>
      <span className="text-muted-foreground"> · </span>
      <span className="text-brand-indigo">{part.kind}</span>
      {dims && <span className="text-muted-foreground"> · {dims}</span>}
      {material && (
        <span className="text-muted-foreground"> · {material.name}</span>
      )}
    </div>
  );
}

function JointRow({ joint }: { joint: Joint }) {
  return (
    <div className="pl-5 text-xs">
      <span className="text-green-600 dark:text-green-400">{joint.id}</span>
      <span className="text-muted-foreground"> · </span>
      <span className="text-brand-indigo">{joint.type}</span>
      <span className="text-muted-foreground"> · </span>
      <span>{joint.fromPartId}</span>
      <span className="text-muted-foreground"> ↔ </span>
      <span>{joint.toPartId}</span>
    </div>
  );
}

function partDims(p: Part, t: (key: string) => string): string | null {
  switch (p.kind) {
    case 'panel':
      return `${p.width}×${p.depth}×${p.thickness}`;
    case 'shelf':
      return `${p.width}×${p.depth}×${p.thickness}`;
    case 'rail':
      return `${p.length}×${p.width}×${p.thickness}`;
    case 'leg':
      return `L=${p.length} ${p.crossSection.shape}`;
    case 'fastener':
      return `${p.size} L=${p.length} ×${p.quantity}`;
    case 'custom':
      return 'custom';
    case 'wall':
      return `${p.width}×${p.height} t${p.thickness}${p.openings.length > 0 ? ` · ${p.openings.length}${t('spec.opening')}` : ''}`;
  }
}
